import React, { useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { useIsWebView } from '../../../shared/hooks';
import { useWebViewLoading } from '../../../shared/hooks/useWebViewLoading';
import { appBridge, postMessageToFlutter } from '../../../shared/services/bridge';

import './PaymentComplete.css';

/**
 * 決済完了画面
 *
 * 決済成功後に表示する画面。
 * inquiry_idはURLパラメータ(:id)から取得する。
 * - /inquiries/:id/payment-complete （GMOコールバック経由）
 * - /payment-complete （フォールバック、inquiry_id不明）
 *
 * 注: フォールバック時は navigate('/inquiries') での遷移を行わない。
 * 過去バグ: homeId を伴わない /inquiries への遷移で、useInquiries のフォールバック
 * homeId='1' が ULID バリデーションに失敗し、IntersectionObserver の無限ループが
 * 発生していた（CloudWatch 2026-04-10 11:50 頃の事象）。
 * フォールバック時は Flutter 側の戻るボタンでの離脱のみ許可する。
 */
const PaymentComplete: React.FC = () => {
  const navigate = useNavigate();
  const { id: inquiryId } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const isWebView = useIsWebView();

  // データ取得なし（即座に表示可能）→ isLoading=false でhideLoadingを即時発火
  const contentRef = useWebViewLoading(false);

  // 決済完了画面では戻るボタンを非表示にする（二重決済防止）
  // ただしフォールバックパス（inquiryId 無し）では Flutter 側の戻るボタンを
  // 残さないとユーザーが画面から離脱できなくなるため、非表示にしない。
  // 併せて Flutter フッターに「アンケートへ進む」ボタンを表示するよう通知する
  // （サイン完了パターン signatureCompleteReady と同じ仕組み）。
  useEffect(() => {
    if (inquiryId) {
      appBridge.hideBackButton();
      postMessageToFlutter('paymentCompleted');
    }
  }, [inquiryId]);

  // inquiry_idがない場合（フォールバック）
  // 遷移先が特定できないためナビゲーションボタンは置かない。
  // ユーザーは Flutter 側ヘッダーの戻るボタンから離脱する。
  if (!inquiryId) {
    return (
      <div ref={contentRef} className="completion-container">
        <main className="completion-content">
          <img src="/assets/images/check-circle.png" alt="完了" className="completion-icon" />
          <h2 className="completion-title">お支払いが完了いたしました</h2>
          <p>ご利用いただきありがとうございました</p>
        </main>
      </div>
    );
  }

  return (
    <div ref={contentRef} className="completion-container">
      <main className="completion-content">
        <img src="/assets/images/check-circle.png" alt="完了" className="completion-icon" />
        <h2 className="completion-title">お支払いが完了いたしました</h2>
        <p>ご利用いただきありがとうございました</p>
        <p>今後のサービス向上のため、アンケートへのご協力をお願いいたします。</p>
      </main>
      {/* WebView 経由の場合は Flutter ネイティブのフッターに同じボタンが出るため
          二重表示を防ぐべく React 側のボタンは非表示にする。
          Web 直接アクセス時のみ React 側のボタンを表示する。 */}
      {!isWebView && (
        <footer className="completion-footer">
          <button
            className="button-primary"
            onClick={() => {
              const token = searchParams.get('token');
              const query = token ? `?token=${token}` : '';
              navigate(`/inquiries/${inquiryId}/survey${query}`);
            }}
          >
            アンケートへ進む
          </button>
        </footer>
      )}
    </div>
  );
}

export default PaymentComplete;
